import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { RadioButton } from "@folio/stripes/components";
import { VolumeStatusBadge, volumeStatusOf, } from "../../common/VolumeStatusBadge";
import { TitleSourceBadge } from "./TitleSourceBadge";
import css from "./MergeDuplicateTitles.module.css";
var INVESTIGATION_LABEL_IDS = {
    IN_PROGRESS: "ui-br-legaldeposits.investigation.status.inProgress",
    RESOLVED: "ui-br-legaldeposits.investigation.status.resolved",
};
var FIELD_ROWS = [
    {
        key: "name",
        labelId: "ui-br-legaldeposits.titleMerge.field.name",
        value: function (title) { var _a; return (_a = title.mainName) !== null && _a !== void 0 ? _a : ""; },
    },
    {
        key: "author",
        labelId: "ui-br-legaldeposits.titleMerge.field.author",
        value: function (title) { var _a; return (_a = title.author) !== null && _a !== void 0 ? _a : ""; },
    },
    {
        key: "identifiers",
        labelId: "ui-br-legaldeposits.titleMerge.field.identifiers",
        value: function (title) { var _a; return ((_a = title.identifiers) !== null && _a !== void 0 ? _a : []).join(", "); },
    },
    {
        key: "publisher",
        labelId: "ui-br-legaldeposits.titleMerge.field.publisher",
        value: function (title) { var _a; return (_a = title.publisher) !== null && _a !== void 0 ? _a : ""; },
    },
    {
        key: "year",
        labelId: "ui-br-legaldeposits.titleMerge.field.year",
        value: function (title) { var _a, _b; return (_b = (_a = title.publicationYear) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : ""; },
    },
    {
        key: "edition",
        labelId: "ui-br-legaldeposits.titleMerge.field.edition",
        value: function (title) { var _a, _b, _c; return (_c = (_a = title.editionStatement) !== null && _a !== void 0 ? _a : (_b = title.editionNumber) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : ""; },
    },
    {
        key: "part",
        labelId: "ui-br-legaldeposits.titleMerge.field.part",
        value: function (title) {
            return [title.partNumber, title.partName].filter(Boolean).join(" / ");
        },
    },
    {
        key: "place",
        labelId: "ui-br-legaldeposits.titleMerge.field.place",
        value: function (title) { var _a; return (_a = title.publicationPlace) !== null && _a !== void 0 ? _a : ""; },
    },
    {
        key: "skc001",
        labelId: "ui-br-legaldeposits.titleMerge.field.skc001",
        value: function (title) { var _a; return (_a = title.skc001) !== null && _a !== void 0 ? _a : ""; },
    },
    {
        key: "note",
        labelId: "ui-br-legaldeposits.titleMerge.field.note",
        value: function (title) { var _a; return (_a = title.libraryNote) !== null && _a !== void 0 ? _a : ""; },
    },
];
export var MergeComparisonTable = function (_a) {
    var titles = _a.titles, survivorId = _a.survivorId, survivorLocked = _a.survivorLocked, canRemove = _a.canRemove, onPickSurvivor = _a.onPickSurvivor, onRemove = _a.onRemove;
    var formatMessage = useIntl().formatMessage;
    var survivor = useMemo(function () { return titles.find(function (title) { return title.id === survivorId; }); }, [titles, survivorId]);
    var investigationValue = useMemo(function () { return function (title) {
        var labelId = INVESTIGATION_LABEL_IDS[title.investigationStatus];
        return labelId ? formatMessage({ id: labelId }) : "";
    }; }, [formatMessage]);
    var renderValue = function (value, isSurvivor, base) {
        if (!value) {
            return React.createElement("span", { className: css.empty }, "\u2014");
        }
        var differs = !isSurvivor && value !== base;
        return React.createElement("span", { className: differs ? css.diff : undefined }, value);
    };
    return (React.createElement("div", { className: css.comparisonWrap },
        React.createElement("table", { className: css.comparison, "data-test-id": "merge-comparison" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { scope: "col" },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.comparison.fieldColumn" })),
                    titles.map(function (title) {
                        var isSurvivor = title.id === survivorId;
                        return (React.createElement("th", { scope: "col", key: title.id, className: isSurvivor ? css.survivorCell : undefined },
                            React.createElement("div", { className: css.columnHeader },
                                React.createElement("div", { className: css.columnHeaderTop },
                                    React.createElement("span", { className: css.columnRole, "data-test-id": "merge-survivor-radio-".concat(title.id) },
                                        React.createElement(RadioButton, { name: "merge-survivor", checked: isSurvivor, disabled: survivorLocked, onChange: function () { return onPickSurvivor(title.id); }, marginBottom0: true, label: formatMessage({
                                                id: isSurvivor
                                                    ? "ui-br-legaldeposits.titleMerge.comparison.survivor"
                                                    : "ui-br-legaldeposits.titleMerge.comparison.absorbed",
                                            }) })),
                                    React.createElement(TitleSourceBadge, { source: title.source, provisional: title.provisional })),
                                React.createElement("div", { className: css.columnHeaderBottom },
                                    React.createElement("span", { className: css.hint }, title.mainName),
                                    canRemove && (React.createElement("button", { type: "button", className: css.removeButton, onClick: function () { return onRemove(title.id); }, "data-test-id": "merge-remove-".concat(title.id) },
                                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.comparison.remove" })))))));
                    }))),
            React.createElement("tbody", null,
                FIELD_ROWS.map(function (row) { return (React.createElement("tr", { key: row.key },
                    React.createElement("td", null,
                        React.createElement(FormattedMessage, { id: row.labelId })),
                    titles.map(function (title) { return (React.createElement("td", { key: title.id, className: title.id === survivorId ? css.survivorCell : undefined }, renderValue(row.value(title), title.id === survivorId, survivor ? row.value(survivor) : ""))); }))); }),
                React.createElement("tr", null,
                    React.createElement("td", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.field.investigation" })),
                    titles.map(function (title) { return (React.createElement("td", { key: title.id, className: title.id === survivorId ? css.survivorCell : undefined }, renderValue(investigationValue(title), title.id === survivorId, survivor ? investigationValue(survivor) : ""))); })),
                React.createElement("tr", null,
                    React.createElement("td", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titleMerge.field.libraryCopies" })),
                    titles.map(function (title) { return (React.createElement("td", { key: title.id, className: title.id === survivorId ? css.survivorCell : undefined }, title.depositStatuses.length === 0 ? (React.createElement("span", { className: css.empty }, "\u2014")) : (title.depositStatuses.map(function (library) { return (React.createElement("div", { key: library.libraryId, className: css.libraryRow },
                        React.createElement("span", null, library.libraryName),
                        React.createElement("span", null, library.statuses.map(function (status) { return (React.createElement(VolumeStatusBadge, { key: status, status: volumeStatusOf(status) })); })))); })))); }))))));
};
